import { useEffect, useRef, useState } from "react";
import { keyCodes } from "../utils/KeyCodes";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

import "overlayscrollbars/overlayscrollbars.css";
import { buildClasses } from "../utils/buildClasses";
import { RyeIcon } from "./RyeIcon";
import { RyeTooltip } from "./RyeTooltip";
import {
  useFloating,
  offset,
  flip,
  shift,
  size as floatingSize,
  useInteractions,
  useDismiss,
  FloatingPortal,
  autoUpdate,
} from "@floating-ui/react";
import { useRyeMenuTransition } from "./useRyeMenuTransition";

type DropDownEntryIcon = null | string | JSX.Element;

export type DropDownEntry<TIdType> = {
  id: TIdType;
  text: string;
  icon: DropDownEntryIcon;
  groupId?: string;
  /** A second, quieter line under the entry's text in the menu. */
  subtitle?: string;
};

export type DropDownGroup = {
  id: string;
  label: string;
};

export function RyeDropDown<TIdType>({
  initEntryId,
  entries,
  groups = [],
  onSelectEntry,
  searchable = false,
  showNoneOption = false,
  noneOptionText = "none",
  placeholder = "",
  onSelectNoneOption = () => {},
  onEnter = () => {},
  onEsc = () => {},
  onEntryClick = () => {},
  onClose = () => {},
  size = "md",
  height = "auto",
  width = "md",
  variant = "outlined",
  fontWeight = "normal",
  alwaysShowArrow = false,
  parentColor = "normal",
  groupHover = false, // add group/ryesearchabledropdown to the parent
  placeholderClassName = "",
  placeholderIcon = null,
  placeholderIconClassName = "",
  hookInputRef = () => {},
  tooltip,
}: {
  initEntryId: TIdType | null;
  entries: DropDownEntry<TIdType>[];
  groups?: DropDownGroup[];
  onSelectEntry: (entry: DropDownEntry<TIdType>) => void;
  searchable?: boolean;
  showNoneOption?: boolean;
  noneOptionText?: string;
  placeholder?: string;
  onSelectNoneOption?: () => void;
  onEnter?: (
    event: React.KeyboardEvent<HTMLDivElement>,
    entry: DropDownEntry<TIdType> | null,
  ) => void;
  onEsc?: () => void;
  onEntryClick?: () => void;
  onClose?: () => void;
  size?: "sm" | "md" | "lg" | "xl";
  height?: "auto" | "full";
  /** "auto" fits the selected entry's text, for a dropdown that reads as a heading. */
  width?: "xs" | "sm" | "md" | "lg" | "auto" | "full";
  variant?: "outlined" | "transparent";
  fontWeight?: "normal" | "medium";
  /** Shows the arrow even when the transparent variant isn't hovered. */
  alwaysShowArrow?: boolean;
  parentColor?: "normal" | "purple";
  groupHover?: boolean;
  placeholderClassName?: string;
  placeholderIcon?: DropDownEntryIcon;
  placeholderIconClassName?: string;
  hookInputRef?: (inputRef: React.RefObject<HTMLInputElement>) => void;
  tooltip?: string;
}): JSX.Element {
  const [selectedEntryId, setSelectedEntryId] = useState<TIdType | null>(
    initEntryId,
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [filterValue, setFilterValue] = useState<string>("");
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null);
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined);

  const { refs, floatingStyles, context } = useFloating({
    open: isExpanded,
    onOpenChange: (open) => {
      if (!open) close();
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip(),
      shift(),
      floatingSize({
        apply({ rects }) {
          setMenuWidth(rects.reference.width);
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const { getFloatingProps } = useInteractions([dismiss]);
  const menuTransition = useRyeMenuTransition(context);

  useEffect(() => {
    setSelectedEntryId(initEntryId);
  }, [initEntryId]);

  function close() {
    setIsExpanded(false);
    setFilterValue("");
    setHighlightIndex(null);
    onClose();
  }

  function selectEntry(entry: DropDownEntry<TIdType>): void {
    setSelectedEntryId(entry.id);
    onSelectEntry(entry);
    close();
  }

  function selectNoneOption() {
    setSelectedEntryId(null);
    onSelectNoneOption();
    close();
  }

  function noneOptionVisible(): boolean {
    return showNoneOption && filterValue === "";
  }

  function handleKeyDown(
    filteredEntries: DropDownEntry<TIdType>[],
    event: React.KeyboardEvent<HTMLDivElement>,
  ): void {
    let lastCategoryIndex = filteredEntries.length - 1;
    if (noneOptionVisible()) {
      lastCategoryIndex++; // none option technically isn't in the filteredEntries
    }
    switch (event.code) {
      case keyCodes.UPARROW:
        event.preventDefault(); // don't want to scroll the transaction row too
        event.stopPropagation();
        if (filteredEntries.length === 0) {
          break;
        } else if (highlightIndex === null || highlightIndex === 0) {
          setHighlightIndex(lastCategoryIndex);
        } else {
          setHighlightIndex(highlightIndex - 1);
        }
        break;
      case keyCodes.DOWNARROW:
        event.preventDefault(); // don't want to scroll the transaction row too
        event.stopPropagation();
        if (filteredEntries.length === 0) {
          break;
        } else if (
          highlightIndex === null ||
          highlightIndex === lastCategoryIndex
        ) {
          setHighlightIndex(0);
        } else {
          setHighlightIndex(highlightIndex + 1);
        }
        break;
      case keyCodes.ENTER:
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) return;
        if (highlightIndex === null) return;
        if (noneOptionVisible() && highlightIndex === 0) {
          selectNoneOption();
          onEnter(event, null);
          return;
        }
        let filteredEntriesIndex = highlightIndex;
        if (noneOptionVisible()) {
          filteredEntriesIndex--; // none option technically isn't in the filteredEntries
        }
        if (
          filteredEntriesIndex !== null &&
          filteredEntriesIndex < filteredEntries.length
        ) {
          selectEntry(filteredEntries[filteredEntriesIndex]);
          onEnter(event, filteredEntries[filteredEntriesIndex]);
        }
        break;
      case keyCodes.ESC:
        event.preventDefault();
        event.stopPropagation();
        if (event.repeat) return;
        close();
        onEsc();
        break;
      case keyCodes.TAB:
        event.stopPropagation();
        if (event.repeat) return;
        close();
        break;
      default:
        break;
    }
  }

  function getSelectedEntry(): DropDownEntry<TIdType> | null {
    const entry = entries.filter((entry) => entry.id === selectedEntryId);
    if (entry.length === 1) {
      return entry[0];
    }
    return null;
  }

  function getHighlightedEntry(
    filteredEntries: DropDownEntry<TIdType>[],
  ): DropDownEntry<TIdType> | null {
    if (highlightIndex === null) {
      return null;
    }
    const entry = filteredEntries.at(highlightIndex);
    if (entry === undefined) {
      return null;
    }
    return entry;
  }

  function hasIcons(): boolean {
    return entries.reduce(
      (hasIcons, entry) => hasIcons || entry.icon !== null,
      false,
    );
  }

  function createFilteredEntriesList(): DropDownEntry<TIdType>[] {
    const filtered = entries.filter((entry: DropDownEntry<TIdType>) =>
      entry.text.toLowerCase().includes(filterValue.toLowerCase()),
    );

    // If no groups are provided, return filtered entries as-is
    if (groups.length === 0) {
      return filtered;
    }

    // When groups are present, return entries in the same order as they appear in the menu
    const orderedEntries: DropDownEntry<TIdType>[] = [];

    // Create group map for quick lookup
    const groupMap = new Map<string, DropDownGroup>();
    groups.forEach((group) => groupMap.set(group.id, group));

    // Group filtered entries by groupId
    const groupedEntries = new Map<string, DropDownEntry<TIdType>[]>();
    const ungroupedEntries: DropDownEntry<TIdType>[] = [];

    filtered.forEach((entry) => {
      if (entry.groupId && groupMap.has(entry.groupId)) {
        if (!groupedEntries.has(entry.groupId)) {
          groupedEntries.set(entry.groupId, []);
        }
        groupedEntries.get(entry.groupId)!.push(entry);
      } else {
        ungroupedEntries.push(entry);
      }
    });

    // Add entries in the same order as renderMenuItems: groups first, then ungrouped
    groups.forEach((group) => {
      const groupEntries = groupedEntries.get(group.id);
      if (groupEntries && groupEntries.length > 0) {
        orderedEntries.push(...groupEntries);
      }
    });

    // Add ungrouped entries at the end
    orderedEntries.push(...ungroupedEntries);

    return orderedEntries;
  }

  const filteredEntries: DropDownEntry<TIdType>[] = createFilteredEntriesList();

  const dropdown = (
    <div
      ref={refs.setReference}
      className={buildClasses(
        {
          switch: width,
          cases: new Map([
            ["xs", "w-28"],
            ["sm", "w-44"],
            ["md", "w-56"],
            ["lg", "w-72"],
            ["auto", "w-fit max-w-full"],
            ["full", "w-full"],
          ]),
        },
        { if: height === "full", then: "h-full" },
        "relative",
      )}
      onKeyDown={(e) => handleKeyDown(filteredEntries, e)} // NOTE: this only fires if the input is focused.
    >
      <Input<TIdType>
        selectedEntry={getSelectedEntry()}
        highlightedEntry={getHighlightedEntry(filteredEntries)}
        isExpanded={isExpanded}
        noEntries={filteredEntries.length === 0}
        hasIcons={hasIcons()}
        searchable={searchable}
        placeholder={placeholder}
        size={size}
        height={height}
        fitText={width === "auto"}
        variant={variant}
        fontWeight={fontWeight}
        alwaysShowArrow={alwaysShowArrow}
        parentColor={parentColor}
        groupHover={groupHover}
        placeholderClassName={placeholderClassName}
        placeholderIcon={placeholderIcon}
        placeholderIconClassName={placeholderIconClassName}
        setFilterValue={setFilterValue}
        setHighlightIndex={setHighlightIndex}
        setIsExpanded={setIsExpanded}
        hookInputRef={hookInputRef}
      />
      {menuTransition.isMounted ? (
        <Menu<TIdType>
          entries={filteredEntries}
          groups={groups}
          noneOptionVisible={noneOptionVisible()}
          noneOptionText={noneOptionText}
          highlightIndex={highlightIndex}
          hasIcons={hasIcons()}
          size={size}
          setHighlightIndex={setHighlightIndex}
          setSelectedEntryId={setSelectedEntryId}
          selectEntry={selectEntry}
          onEntryClick={onEntryClick}
          selectNoneOption={selectNoneOption}
          floatingRef={refs.setFloating}
          floatingStyles={floatingStyles}
          floatingProps={getFloatingProps()}
          transitionStyles={menuTransition.styles}
          transitionClassName={menuTransition.className}
          menuWidth={menuWidth}
          fitsText={width === "auto"}
        ></Menu>
      ) : null}
    </div>
  );

  if (tooltip && !isExpanded) {
    return <RyeTooltip text={tooltip}>{dropdown}</RyeTooltip>;
  }

  return dropdown;
}

function Input<TIdType>(props: {
  selectedEntry: DropDownEntry<TIdType> | null;
  highlightedEntry: DropDownEntry<TIdType> | null;
  isExpanded: boolean;
  noEntries: boolean;
  hasIcons: boolean;
  searchable: boolean;
  placeholder: string;
  size: string;
  height: "auto" | "full";
  fitText: boolean;
  variant: "outlined" | "transparent";
  fontWeight: "normal" | "medium";
  alwaysShowArrow: boolean;
  parentColor: "normal" | "purple";
  groupHover: boolean;
  placeholderClassName: string;
  placeholderIcon: DropDownEntryIcon;
  placeholderIconClassName: string;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  setFilterValue: React.Dispatch<React.SetStateAction<string>>;
  setHighlightIndex: React.Dispatch<React.SetStateAction<number | null>>;
  hookInputRef: (inputRef: React.RefObject<HTMLInputElement>) => void;
}): JSX.Element {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // Block body on purpose: an expression body would forward hookInputRef's
  // return value to React as this effect's cleanup. The prop is typed
  // "=> void", but TypeScript's void-return bivariance lets a handler return
  // anything, which would throw "destroy is not a function" on unmount.
  useEffect(() => {
    props.hookInputRef(inputRef);
  }, [props]);

  useEffect(() => {
    if (props.isExpanded) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [props.isExpanded, props.searchable]);

  function handleInputChanged(value: string) {
    if (!props.searchable) {
      return;
    }
    setInputValue(value);
    props.setFilterValue(value);
    props.setHighlightIndex(0);
  }

  function handleFocus() {
    props.setIsExpanded(true);
    setInputValue(getInputValue());
    if (props.searchable) {
      inputRef.current?.select();
    }
  }

  function handleMouseDown(e: React.MouseEvent<HTMLInputElement, MouseEvent>) {
    if (!props.searchable && props.isExpanded) {
      props.setIsExpanded(false);
      e.preventDefault(); // we don't want to just immediately re-open the drop down by focusing on the click
    }
  }

  function handleIconOrArrowMouseDown(
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) {
    if (props.isExpanded) {
      props.setIsExpanded(false);
      e.preventDefault(); // we don't want to just immediately re-open the drop down by focusing on the click
    } else {
      inputRef.current?.focus();
      e.preventDefault();
    }
  }

  function getInputValue(): string {
    if (!props.isExpanded) {
      return props.selectedEntry?.text ?? "";
    }
    return inputValue;
  }

  function getIconValue(): DropDownEntryIcon {
    if (!props.isExpanded) {
      return props.selectedEntry?.icon ?? props.placeholderIcon ?? null;
    }
    if (props.noEntries) {
      return "";
    }
    if (props.highlightedEntry === null && props.selectedEntry !== null) {
      return props.selectedEntry.icon;
    }
    return "";
  }

  // What sets the text's look and the room around it, shared with the copy of
  // the text that sizes a dropdown fitted to its text.
  const textClasses = buildClasses(
    {
      if: props.hasIcons,
      then: buildClasses("pl-10"),
      else: buildClasses("pl-3"),
    },
    {
      switch: props.size,
      cases: new Map([
        ["sm", buildClasses("text-sm")],
        ["md", buildClasses("text-md")],
        ["lg", buildClasses("text-lg")],
        ["xl", buildClasses("text-xl")],
      ]),
    },
    { if: props.fontWeight === "medium", then: "font-medium" },
    "px-3",
    "pr-8",
    "border",
  );

  return (
    <div
      className={buildClasses(
        { if: props.height === "full", then: "h-full" },
        {
          // The input and an invisible copy of its text share one grid cell,
          // so the copy's width sets the input's.
          if: props.fitText,
          then: buildClasses("inline-grid", "max-w-full"),
          else: "w-full",
        },
        "relative",
        "group/ryesearchabledropdowninput",
      )}
    >
      {props.fitText && (
        <span
          aria-hidden
          className={buildClasses(
            textClasses,
            "[grid-area:1/1]",
            "invisible",
            "whitespace-pre",
            "overflow-hidden",
          )}
        >
          {getInputValue() || props.placeholder}
        </span>
      )}
      <input
        type="text"
        ref={inputRef}
        className={buildClasses(
          {
            // No width of its own, so the text's copy alone sizes the cell
            // the input then fills.
            if: props.fitText,
            then: buildClasses("[grid-area:1/1]", "w-0", "min-w-full"),
            else: "w-full",
          },
          {
            if: !props.searchable,
            then: "caret-transparent",
            else: "focus:cursor-text",
          },
          {
            if: props.noEntries && props.isExpanded,
            then: buildClasses(
              "bg-red-50",
              "focus:border-red",
              "focus:shadow-red/20",
            ),
            else: buildClasses(
              {
                switch: props.parentColor,
                cases: new Map([
                  ["normal", "bg-transparent"],
                  ["purple", "bg-purple-50"],
                ]),
              },
              "focus:border-purple",
              "focus:shadow-purple-500/20",
            ),
          },
          textClasses,
          {
            switch: props.height,
            cases: new Map([
              [
                "auto",
                buildClasses({
                  switch: props.size,
                  cases: new Map([
                    ["sm", buildClasses("h-7")],
                    ["md", buildClasses("h-9")],
                    ["lg", buildClasses("h-11")],
                    ["xl", buildClasses("h-11")],
                  ]),
                }),
              ],
              ["full", buildClasses("h-full")],
            ]),
          },
          {
            switch: props.variant,
            cases: new Map([
              [
                "outlined",
                buildClasses({
                  switch: props.parentColor,
                  cases: new Map([
                    ["normal", "border-on-surface-300"],
                    ["purple", "border-purple-200"],
                  ]),
                }),
              ],
              [
                "transparent",
                buildClasses(
                  "border-transparent",
                  "focus:border",
                  "hover:border",
                  {
                    switch: props.parentColor,
                    cases: new Map([
                      ["normal", "hover:border-on-surface-200"],
                      ["purple", "hover:border-purple-200"],
                    ]),
                  },
                ),
              ],
            ]),
          },
          {
            if: props.groupHover && !props.isExpanded,
            then: buildClasses(
              "group-hover/ryesearchabledropdown:border",
              "group-hover/ryesearchabledropdown:border-on-surface-200",
            ),
          },
          "outline-none",
          "box-border",
          "text-start",
          "rounded-md",
          "focus:shadow-glow",
          "text-md",
          "overflow-hidden",
          "whitespace-nowrap",
          "text-ellipsis",
          "hover:cursor-pointer",
          "transition-all",
          props.placeholderClassName,
        )}
        value={getInputValue()}
        placeholder={props.placeholder}
        spellCheck="false"
        onFocus={handleFocus}
        onChange={(e) => handleInputChanged(e.target.value)}
        onMouseDown={(e) => handleMouseDown(e)}
      />
      <EntryIcon
        icon={getIconValue()}
        handleIconOrArrowMouseDown={handleIconOrArrowMouseDown}
        className={
          !props.isExpanded && !props.selectedEntry
            ? props.placeholderIconClassName
            : undefined
        }
      />
      <ArrowIcon
        inputIsFocused={props.isExpanded}
        variant={props.variant}
        alwaysShow={props.alwaysShowArrow}
        groupHover={props.groupHover}
        searchable={props.searchable}
        isExpanded={props.isExpanded}
        setIsExpanded={props.setIsExpanded}
        handleIconOrArrowMouseDown={handleIconOrArrowMouseDown}
      />
    </div>
  );
}

function Menu<TIdType>(props: {
  entries: DropDownEntry<TIdType>[];
  groups: DropDownGroup[];
  noneOptionVisible: boolean;
  noneOptionText: string;
  highlightIndex: number | null;
  hasIcons: boolean;
  size: string;
  setHighlightIndex: React.Dispatch<React.SetStateAction<number | null>>;
  setSelectedEntryId: React.Dispatch<React.SetStateAction<TIdType | null>>;
  selectEntry: (entry: DropDownEntry<TIdType>) => void;
  onEntryClick: () => void;
  selectNoneOption: () => void;
  floatingRef: (node: HTMLElement | null) => void;
  floatingStyles: React.CSSProperties;
  floatingProps: Record<string, unknown>;
  transitionStyles: React.CSSProperties;
  transitionClassName: string;
  menuWidth: number | undefined;
  /** Whether the dropdown fits its text, so the menu may be wider than it. */
  fitsText: boolean;
}): JSX.Element {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (props.highlightIndex !== null) {
      scrollItemIntoView(props.highlightIndex);
    }
  }, [props.highlightIndex]);

  function scrollItemIntoView(itemIndex: number) {
    itemRefs.current[itemIndex]?.scrollIntoView({
      block: "nearest",
      inline: "nearest",
      behavior: "smooth",
    });
  }

  function renderMenuItems(): JSX.Element[] {
    const items: JSX.Element[] = [];
    let currentIndex = 0;

    // Add none option if visible
    if (props.noneOptionVisible) {
      items.push(
        <MenuItem<string>
          key={"none_option"}
          index={0}
          entry={{
            id: "none_option",
            text: props.noneOptionText,
            icon: props.hasIcons ? "--" : null,
          }}
          isHighlighted={props.highlightIndex === 0}
          isNoneOption={true}
          hasIcons={props.hasIcons}
          itemRefs={itemRefs}
          size={props.size}
          onClick={() => {
            props.selectNoneOption();
            props.onEntryClick();
          }}
        />,
      );
      currentIndex = 1;
    }

    // If no groups are provided, render entries without grouping
    if (props.groups.length === 0) {
      props.entries.forEach((entry, i) => {
        items.push(
          <MenuItem<TIdType>
            key={`drop_down_entry_${entry.id}`}
            index={currentIndex + i}
            entry={entry}
            isHighlighted={currentIndex + i === props.highlightIndex}
            isNoneOption={false}
            hasIcons={props.hasIcons}
            itemRefs={itemRefs}
            size={props.size}
            onClick={() => {
              props.selectEntry(entry);
              props.onEntryClick();
            }}
          />,
        );
      });
      return items;
    }

    // Group entries by groupId
    const groupMap = new Map<string, DropDownGroup>();
    props.groups.forEach((group) => groupMap.set(group.id, group));

    const groupedEntries = new Map<string, DropDownEntry<TIdType>[]>();
    const ungroupedEntries: DropDownEntry<TIdType>[] = [];

    props.entries.forEach((entry) => {
      if (entry.groupId && groupMap.has(entry.groupId)) {
        if (!groupedEntries.has(entry.groupId)) {
          groupedEntries.set(entry.groupId, []);
        }
        groupedEntries.get(entry.groupId)!.push(entry);
      } else {
        ungroupedEntries.push(entry);
      }
    });

    // Render groups in the order they appear in props.groups
    props.groups.forEach((group) => {
      const groupEntries = groupedEntries.get(group.id);
      if (groupEntries && groupEntries.length > 0) {
        // Add group header
        items.push(
          <div
            key={`group_header_${group.id}`}
            className={buildClasses(
              "px-3",
              "py-2",
              "text-xs",
              "font-semibold",
              "text-on-surface-500",
              "bg-surface-100",
              "border-on-surface-100",
            )}
          >
            {group.label}
          </div>,
        );

        // Add group entries
        groupEntries.forEach((entry, i) => {
          items.push(
            <MenuItem<TIdType>
              key={`drop_down_entry_${entry.id}`}
              index={currentIndex}
              entry={entry}
              isHighlighted={currentIndex === props.highlightIndex}
              isNoneOption={false}
              hasIcons={props.hasIcons}
              itemRefs={itemRefs}
              size={props.size}
              onClick={() => {
                props.selectEntry(entry);
                props.onEntryClick();
              }}
            />,
          );
          currentIndex++;
        });
      }
    });

    // Add ungrouped entries at the end
    if (ungroupedEntries.length > 0) {
      ungroupedEntries.forEach((entry, i) => {
        items.push(
          <MenuItem<TIdType>
            key={`drop_down_entry_${entry.id}`}
            index={currentIndex}
            entry={entry}
            isHighlighted={currentIndex === props.highlightIndex}
            isNoneOption={false}
            hasIcons={props.hasIcons}
            itemRefs={itemRefs}
            size={props.size}
            onClick={() => {
              props.selectEntry(entry);
              props.onEntryClick();
            }}
          />,
        );
        currentIndex++;
      });
    }

    return items;
  }

  return (
    <FloatingPortal>
      <div
        ref={(node) => {
          menuRef.current = node;
          props.floatingRef(node);
        }}
        style={{
          ...props.floatingStyles,
          ...(props.fitsText
            ? { minWidth: props.menuWidth }
            : { width: props.menuWidth }),
        }}
        {...props.floatingProps}
        className={buildClasses("z-[2000000000]", {
          if: props.fitsText,
          then: buildClasses("min-w-60", "max-w-sm"),
        })}
      >
        {/* Animated apart from the outer element, whose transform places it. */}
        <div
          style={props.transitionStyles}
          className={props.transitionClassName}
        >
          {/* options described at https://kingsora.github.io/OverlayScrollbars/ */}
          <OverlayScrollbarsComponent
            options={{
              scrollbars: { autoHide: "never" },
              overflow: { x: "hidden" },
            }}
            className={buildClasses(
              {
                if: props.entries.length > 0,
                then: buildClasses("border-tiny", "border-on-surface-200"),
                else: buildClasses("border-none"),
              },
              "w-full",
              "overflow-hidden" /* hides extra material from buttons without border radius */,
              "rounded-md",
              "max-h-48",
              "overflow-y-auto",
              "shadow-popover",
            )}
          >
            {renderMenuItems()}
          </OverlayScrollbarsComponent>
        </div>
      </div>
    </FloatingPortal>
  );
}

function MenuItem<TIdType>(props: {
  index: number;
  entry: DropDownEntry<TIdType>;
  isHighlighted: boolean;
  isNoneOption: boolean;
  hasIcons: boolean;
  itemRefs: React.MutableRefObject<(HTMLDivElement | null)[]>;
  size: string;
  onClick: () => void;
}): JSX.Element {
  return (
    <div
      ref={(ref) => (props.itemRefs.current[props.index] = ref)}
      className={buildClasses(
        {
          if: props.isHighlighted,
          then: buildClasses("bg-on-surface-100"),
          else: buildClasses("bg-surface-50", "hover:bg-on-surface-50"),
        },
        {
          if: props.isNoneOption,
          then: buildClasses("text-on-surface-400"),
        },
        {
          if: props.hasIcons,
          then: buildClasses("pl-10"),
          else: buildClasses("pl-3"),
        },
        {
          switch: props.size,
          cases: new Map([
            ["sm", buildClasses("text-sm")],
            ["md", buildClasses("text-md")],
            ["lg", buildClasses("text-lg")],
            // A heading-sized dropdown's menu reads at the usual size.
            ["xl", buildClasses("text-md")],
          ]),
        },
        {
          // With a second line, the item grows to fit both.
          if: props.entry.subtitle !== undefined,
          then: "py-1.5",
          else: buildClasses({
            switch: props.size,
            cases: new Map([
              ["sm", "h-8"],
              ["md", "h-9"],
              ["lg", "h-11"],
              ["xl", "h-9"],
            ]),
          }),
        },
        "relative",
        "flex",
        "items-center",
        "w-full",
        "px-3",
        "text-left",
        "border-none",
        "cursor-pointer",
        "rounded-none",
        "cursor-pointer",
      )}
      onClick={props.onClick}
      tabIndex={-1}
    >
      <EntryIcon
        icon={props.entry.icon}
        handleIconOrArrowMouseDown={() => {}}
      />
      <div className={buildClasses("flex", "flex-col", "min-w-0")}>
        <div
          className={buildClasses(
            "block",
            "overflow-hidden",
            "whitespace-nowrap",
            "text-ellipsis",
          )}
        >
          {props.entry.text}
        </div>
        {props.entry.subtitle !== undefined && (
          <div className={buildClasses("text-xs", "text-on-surface-500")}>
            {props.entry.subtitle}
          </div>
        )}
      </div>
    </div>
  );
}

function EntryIcon(props: {
  icon: DropDownEntryIcon;
  className?: string;
  handleIconOrArrowMouseDown: (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => void;
}): JSX.Element | null {
  if (props.icon === null) {
    return null;
  }
  return (
    <div
      className={buildClasses(
        "flex",
        "items-center",
        "justify-center",
        "absolute",
        "top-1/2",
        "-translate-y-1/2",
        "left-3",
        "z-0",
        "cursor-pointer",
        props.className ?? "",
      )}
      onMouseDown={props.handleIconOrArrowMouseDown}
    >
      {props.icon}
    </div>
  );
}

function ArrowIcon(props: {
  inputIsFocused: boolean;
  variant: "outlined" | "transparent";
  alwaysShow: boolean;
  groupHover: boolean;
  searchable: boolean;
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  handleIconOrArrowMouseDown: (
    e: React.MouseEvent<HTMLInputElement, MouseEvent>,
  ) => void;
}) {
  return (
    <div
      className={buildClasses(
        { if: !props.inputIsFocused, then: "cursor-pointer" },
        { if: props.inputIsFocused, then: "-scale-y-100" },
        {
          if:
            props.variant === "transparent" &&
            !props.inputIsFocused &&
            !props.alwaysShow,
          then: buildClasses(
            "invisible",
            "group-hover/ryesearchabledropdowninput:visible",
          ),
        },
        {
          if: props.groupHover,
          then: buildClasses("group-hover/ryesearchabledropdown:visible"),
        },
        "cursor-pointer",
        "h-full",
        "flex",
        "items-center",
        "absolute",
        "top-1/2",
        "-translate-y-1/2",
        "right-2",
        "transition-all",
        "select-none",
      )}
      onMouseDown={props.handleIconOrArrowMouseDown}
    >
      <RyeIcon
        name={"keyboard_arrow_down"}
        className={buildClasses("text-on-surface-500")}
      />
    </div>
  );
}
